import { Box, Button, Card, CardActionArea, CardActions, CardContent, CardMedia, Typography, withStyles, WithStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { History } from "history";
import * as React from "react";
import { Event } from "../../generated/client";
import strings from "../../localization/strings";
import styles from "../../styles/event/event-lister";
import { CustomStyles } from "../../types";
import EventUtils from "../../utils/event-utils";
import { withCustomStyles } from "../hocs/with-custom-styles";

/**
 * Interface describing component properties
 */
interface Props extends WithStyles<typeof styles> {
  events: Event[];
  loading: boolean;
  disabled: boolean;
  showEditButtons: boolean;
  title: string | JSX.Element;
  customStyles?: CustomStyles;
  history: History<History.LocationState>;
  loadMoreButtonText?: string | JSX.Element;
  editButtonText?: string;
  copyButtonText?: string;
  deleteButtonText?: string;
  loadMoreEvents?: () => void;
  onDeleteClick?: (event: Event) => void;
  onEditClick?: (event: Event) => void;
  onCopyClick?: (event: Event) => void;
}

/**
 * Interface describing component state
 */
interface State { }

/**
 * Component for event lister
 * 
 * @param props component props
 */
class EventLister extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = { };
  }

  /**
   * Component render
   */
  public render = () => {
    const { classes, customStyles, title, loading } = this.props;

    if (loading) {
      return (
        <div
          className={ classes.container }
          style={ customStyles?.container }
        >
          <Typography
            variant={ "h2" }
            className={ classes.title }
            style={ customStyles?.title }
          >
          { title }
          </Typography>
          <div className={ classes.grid }
          style={ customStyles?.grid }>
            <Card 
              className={ classes.loaderCard }
              style={ customStyles?.loaderCard }
            >
              <Box 
                className={ classes.loaderPlaceholder } 
                style={ customStyles?.loaderPlaceholder }
              />
            </Card>
            <Card 
              className={ classes.loaderCard }
              style={ customStyles?.loaderCard }
            >
              <Box 
                className={ classes.loaderPlaceholder } 
                style={ customStyles?.loaderPlaceholder }
              />
            </Card>
            <Card 
              className={ classes.loaderCard }
              style={ customStyles?.loaderCard }
            >
              <Box 
                className={ classes.loaderPlaceholder } 
                style={ customStyles?.loaderPlaceholder }
              />
            </Card>
            <Card 
              className={ classes.loaderCard }
              style={ customStyles?.loaderCard }
            >
              <Box 
                className={ classes.loaderPlaceholder } 
                style={ customStyles?.loaderPlaceholder }
              />
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div
        className={ classes.container }
        style={ customStyles?.container }
      >
        <Typography
          variant={ "h2" }
          className={ classes.title }
          style={ customStyles?.title }
        >
          { title }
        </Typography>
        <div
          className={ classes.grid }
          style={ customStyles?.grid }
        >
          { this.renderEventCards() }
        </div>
        { this.renderLoadMoreButton() }
      </div>
    );
  }

  /**
   * Method for rendering event cards
   */
  private renderEventCards = () => {
    const { customStyles, classes, events } = this.props;

    return events.map((event: Event, index: number) => {
      const id = EventUtils.getEventId(event);
      const eventName = EventUtils.getEventName(event);
      const eventImageSrc = EventUtils.getEventImageSrc(event);
      const eventDuration = EventUtils.getEventDuration(event);
      const eventProviderName = EventUtils.getEventProviderName(event);

      return (
        <Card
          key={ index }
          className={ classes.card }
          style={ customStyles?.card }
        >
          <CardActionArea onClick={ this.toEventPage(id) }>
            <CardMedia
              alt={ strings.generic.imageAltText }
              component="img"
              image={ eventImageSrc }
              className={ classes.media }
              style={ customStyles?.media }
            />
            <CardContent>
              <div>
                <Typography gutterBottom 
                  className={ classes.eventTime }
                  style={ customStyles?.eventTime }
                  variant="caption"
                >
                  { eventDuration }
                </Typography>
                <Typography 
                  className={ classes.eventTitle } 
                  style={ customStyles?.eventTitle }
                  gutterBottom variant="h5"
                >
                  { eventName }
                </Typography>
              </div>
              <Typography gutterBottom 
                className={ classes.eventInfo } 
                style={ customStyles?.eventInfo }
                variant="caption"
              >
                { eventProviderName }
              </Typography>
            </CardContent>
          </CardActionArea>
          { this.renderEventCardEditButtons(event) }
        </Card>
      );
    });
  }

  /**
   * Renders event card edit buttons
   */
  private renderEventCardEditButtons = (event: Event) => {
    const { showEditButtons } = this.props;

    if (!showEditButtons) {
      return null;
    }

    return (
      <CardActions>
        { this.renderEventCardCopyButton(event) }
        { this.renderEventCardEditButton(event) }
        { this.renderEventCardDeleteButton(event) }
      </CardActions>
    );
    
  }

  /**
   * Renders event card copy button
   */
  private renderEventCardCopyButton = (event: Event) => {
    const { onCopyClick, copyButtonText } = this.props;

    if (!onCopyClick || !copyButtonText) {
      return null;
    }
    
    return (
      <Button onClick={ () => onCopyClick(event) }>
        { copyButtonText }
      </Button>
    );
  }

  /**
   * Renders event card edit button
   */
  private renderEventCardEditButton = (event: Event) => {
    const { onEditClick, editButtonText } = this.props;

    if (!onEditClick || !editButtonText) {
      return null;
    }
    
    return (
      <Button onClick={ () => onEditClick(event) }>
        { editButtonText }
      </Button>
    );
  }

  /**
   * Renders event card delete button
   */
  private renderEventCardDeleteButton = (event: Event) => {
    const { onDeleteClick, deleteButtonText } = this.props;

    if (!onDeleteClick || !deleteButtonText) {
      return null;
    }
    
    return (
      <Button onClick={ () => onDeleteClick(event) }>
        { deleteButtonText }
      </Button>
    );
  }

  /**
   * Method for rendering load button
   */
  private renderLoadMoreButton = () => {
    const { classes, customStyles, loadMoreEvents, disabled, loadMoreButtonText, loading } = this.props;

    if (loading || !loadMoreButtonText) {
      return null;
    }

    return (
      <div
        className={ classes.actions }
        style={ customStyles?.actions }
      >
        <Button 
          color="primary"
          onClick={ loadMoreEvents }
          endIcon={ <AddIcon /> }
          disabled={ disabled }
        >
          { loadMoreButtonText }
        </Button>
      </div>
    );
  }

  /**
   * Method for navigating to single event page
   *
   * @param id event id
   */
  private toEventPage = (id: string) => () => {
    const { history } = this.props;

    history.push(`/event/${ id }`);
  }
}

const Styled = withStyles(styles)(EventLister);
const CustomStyled = withCustomStyles("event/event-lister")(Styled);

export default CustomStyled;
